import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  startAfter,
  type DocumentData,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase.js';

export interface LiveState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Live single-document read via the Firestore client SDK, guarded by security rules — this is
 * the "reads" half of the hybrid data-access model in docs/PLAN.md §2.1. Writes never go
 * through this file; see lib/api.ts.
 */
export function useDocument<T = DocumentData>(path: string | null): LiveState<T> {
  const [state, setState] = useState<LiveState<T>>({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    const unsub = onSnapshot(
      doc(db, path),
      (snap) => setState({ data: (snap.data() as T) ?? null, loading: false, error: null }),
      (error) => setState({ data: null, loading: false, error }),
    );
    return unsub;
  }, [path]);

  return state;
}

export interface LiveCollectionState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

function useLiveQuery<T>(
  buildQuery: (() => Query) | null,
  depKey: string,
  mapDoc: (d: QueryDocumentSnapshot) => T,
): LiveCollectionState<T> {
  const [state, setState] = useState<LiveCollectionState<T>>({ data: [], loading: true, error: null });

  useEffect(() => {
    if (!buildQuery) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    const unsub = onSnapshot(
      buildQuery(),
      (snap) => setState({ data: snap.docs.map(mapDoc), loading: false, error: null }),
      (error) => setState({ data: [], loading: false, error }),
    );
    return unsub;
    // depKey stands in for the query's real inputs — buildQuery/mapDoc are fresh closures every
    // render and are deliberately excluded so this doesn't resubscribe on every render.
  }, [depKey]);

  return state;
}

const dataOnly = <T,>(d: QueryDocumentSnapshot) => d.data() as T;

/** Live collection read. `constraints` is recomputed on every render, so callers should memoize
 * an array with useMemo (or pass a stable empty array) to avoid re-subscribing every render. */
export function useCollection<T = DocumentData>(
  path: string | null,
  constraints: QueryConstraint[] = [],
): LiveCollectionState<T> {
  const constraintsKey = constraints.map((c) => c.type).join(',');
  return useLiveQuery<T>(
    path ? () => query(collection(db, path), ...constraints) : null,
    `${path ?? ''}|${constraintsKey}`,
    dataOnly<T>,
  );
}

/** One collection read inside useMergedCollections. */
export interface CollectionSource {
  path: string;
  constraints: QueryConstraint[];
}

/**
 * Live read over several collections at once, merged into one list with `compare` and cut to
 * `max`. For a feed that spans collections no single query can cover — e.g. the dashboard's
 * activity feed for someone who isn't an org admin, which has to read each project's own
 * activity subcollection because the security rules gate each one on that project's role.
 * `key` must change whenever `sources` does (same contract as useCollection's depKey).
 */
export function useMergedCollections<T>(
  sources: CollectionSource[],
  key: string,
  compare: (a: T, b: T) => number,
  max: number,
): LiveCollectionState<T> {
  const [byPath, setByPath] = useState<Record<string, T[]>>({});
  const [pending, setPending] = useState(sources.length);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setByPath({});
    setPending(sources.length);
    setError(null);
    const loaded = new Set<string>();
    const unsubs = sources.map(({ path, constraints }) =>
      onSnapshot(
        query(collection(db, path), ...constraints),
        (snap) => {
          setByPath((prev) => ({ ...prev, [path]: snap.docs.map((d) => d.data() as T) }));
          if (!loaded.has(path)) {
            loaded.add(path);
            setPending((n) => n - 1);
          }
        },
        (err) => {
          setError(err);
          if (!loaded.has(path)) {
            loaded.add(path);
            setPending((n) => n - 1);
          }
        },
      ),
    );
    return () => unsubs.forEach((u) => u());
    // `key` stands in for `sources` — see the doc comment.
  }, [key]);

  const data = useMemo(
    () => Object.values(byPath).flat().sort(compare).slice(0, max),
    [byPath, compare, max],
  );
  return { data, loading: pending > 0, error };
}

/**
 * One page of older entries across the same `sources` a useMergedCollections feed reads —
 * fetched once (not live), starting after `cursor`, the sort-field value of the oldest entry
 * already shown. Each source's constraints must end in the orderBy the cursor refers to, with no
 * limit; Firestore applies the cursor and the per-source limit server-side, so only one page per
 * source is ever read. `hasMore` is false once every source has run dry.
 */
export async function fetchMergedPage<T>(
  sources: CollectionSource[],
  cursor: unknown,
  pageSize: number,
  compare: (a: T, b: T) => number,
): Promise<{ data: T[]; hasMore: boolean }> {
  const snaps = await Promise.all(
    sources.map(({ path, constraints }) =>
      getDocs(query(collection(db, path), ...constraints, startAfter(cursor), limit(pageSize))),
    ),
  );
  const all = snaps.flatMap((s) => s.docs.map((d) => d.data() as T)).sort(compare);
  // Anything cut by the slice is fetched again next time, since the cursor only moves as far as
  // the oldest entry actually returned.
  const hasMore = all.length > pageSize || snaps.some((s) => s.size === pageSize);
  return { data: all.slice(0, pageSize), hasMore };
}

/** One document from a collectionGroup query, plus the id of its parent (e.g. a task's project
 * id) — `data()` alone doesn't carry path information, and callers of a cross-project query
 * like "my tasks" need it to link back to the right project. `parentId` is the id of the
 * document TWO levels up (.../{parentId}/{collectionId}/{docId}), which for
 * projects/{projectId}/tasks/{taskId} is the project id. */
export type WithParentId<T> = T & { parentId: string | null };

/** Live collectionGroup read — e.g. "every task assigned to me, across every project". Requires
 * a Firestore rule with a self-contained clause matching the query's filter (not just an
 * ancestor lookup) or Firestore refuses the query outright — see firestore.rules on tasks. */
export function useCollectionGroup<T = DocumentData>(
  collectionId: string | null,
  constraints: QueryConstraint[] = [],
): LiveCollectionState<WithParentId<T>> {
  const constraintsKey = constraints.map((c) => c.type).join(',');
  return useLiveQuery<WithParentId<T>>(
    collectionId ? () => query(collectionGroup(db, collectionId), ...constraints) : null,
    `${collectionId ?? ''}|${constraintsKey}`,
    (d) => ({ ...(d.data() as T), parentId: d.ref.parent.parent?.id ?? null }),
  );
}
